<script setup>
import { computed } from 'vue'

const props = defineProps({
  activePlans: {
    type: Array,
    default: () => []
  },
  hasBasicMethods: {
    type: Boolean,
    default: false
  },
  hasSilverMethods: {
    type: Boolean,
    default: false
  },
  hasGoldMethods: {
    type: Boolean,
    default: false
  }
})

const hasMultiplePlans = computed(() => props.activePlans.length > 1)

const planTier = computed(() => {
  if (props.hasGoldMethods) return 'gold'
  if (props.hasSilverMethods) return 'silver'
  if (props.hasBasicMethods) return 'basic'
  return null
})

/**
 * The plans, under the names the site sells them by. Silver is Bindle Live; the
 * library-only plan is not sold on its own any more, so it is not listed as a
 * tier a tutor can land on.
 */
const planTierName = computed(() => {
  switch (planTier.value) {
    case 'gold': return '1:1 Tutoring'
    case 'silver': return 'Bindle Live'
    case 'basic': return 'Content Library'
    default: return 'No Plan'
  }
})
</script>

<template>
  <section id="pricing-info" class="surface-card mb-6 p-6">
    <div id="multi-module-info">
      <div class="flex items-start space-x-4 mb-6">
        <div class="grid size-12 shrink-0 place-items-center rounded-xl bg-brand">
          <span class="material-symbols-outlined text-xl text-brand-contrast">layers</span>
        </div>
        <div>
          <h4 class="mb-2 text-lg font-bold text-ink">Your Pricing Plan{{ hasMultiplePlans ? 's' : '' }}</h4>
          <p v-if="hasMultiplePlans" class="text-sm text-muted">
            You've selected methods across <strong>{{ activePlans.length }} tiers</strong>. You'll set pricing for each tier in the next step.
          </p>
          <p v-else-if="planTier" class="text-sm text-muted">
            Based on your selection, you'll be on the <strong>{{ planTierName }}</strong>
          </p>
          <p v-else class="text-sm text-muted">Select your teaching methods to see your pricing plan</p>
        </div>
      </div>
      <div class="space-y-3">
        <div
          class="flex items-center justify-between p-3 rounded-lg transition-all"
          :class="hasSilverMethods ? 'border-2 border-live bg-live-soft' : 'border-2 border-transparent bg-surface-2'"
        >
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined" :class="hasSilverMethods ? 'text-live-soft-text' : 'text-muted'">sensors</span>
            <span class="text-sm font-semibold" :class="hasSilverMethods ? 'text-live-soft-text' : 'text-ink'">Bindle Live</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-muted">A weekly live class</span>
            <span v-if="hasSilverMethods" class="material-symbols-outlined text-live-soft-text">check_circle</span>
          </div>
        </div>
        <div
          class="flex items-center justify-between p-3 rounded-lg transition-all"
          :class="hasGoldMethods ? 'border-2 border-brand bg-brand-soft' : 'border-2 border-transparent bg-surface-2'"
        >
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined" :class="hasGoldMethods ? 'text-brand' : 'text-muted'">diamond</span>
            <span class="text-sm font-semibold" :class="hasGoldMethods ? 'text-brand-soft-text' : 'text-ink'">1:1 Tutoring</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-muted">A weekly one-to-one lesson</span>
            <span v-if="hasGoldMethods" class="material-symbols-outlined text-brand">check_circle</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
